import React, { FC, useMemo, } from 'react';
import cls from 'classnames';
import { safeToFormat } from '@/utils/number';
import { NoSSR, useI18n } from '@spot-fe/base-ui';
import { Block } from '@/components/Layout';
import styles from './index.module.scss';

import { Select, Image } from '@bfe/moly';
import { useMarket } from '@/store/market';
import { useCheckUser } from '@/app/biz';
import {
  tradeRuleClick,
  faqClick,
  userGuideClick,
  newUserGuideClick,
} from '@/config/sensor';
import { replaceDomainToLocation } from '@spot-fe/utils';

interface AcitonProps {
  icon: string;
  text: string;
  isNew?: boolean;
  onAction?: () => void;
}

const DEFAULTMORE = 'DEFAULT_MORE'

const Action: FC<AcitonProps> = ({ icon, text = '', isNew, onAction }) => {
  // console.log('Action:', text, isNew);

  return (
    <span
      className={cls(styles.action, { [styles.isNew]: isNew })}
      onClick={() => {
        if (onAction) {
          onAction();
        }
      }}
    >
      <Image className={styles.icon} src={icon} alt="" />
      <span className={styles.txt}>{text}</span>
    </span>
  );
};

interface TabItem {
  label: string;
  key: string;
  isHot?: boolean;
  // children: React.ReactNode;
}

interface TabsProps {
  items: TabItem[];
  activeKey?: string;
  onChange?: (key: string) => void;
}


// items
const Tabs: FC<TabsProps> = ({ items, activeKey, onChange }) => {
  // const [current, setCurrent] = useState(activeKey);
  const { t } = useI18n()
  const current = activeKey;

  const hiddenCoinList = useMemo(() => {
    if (items.length > 7) {
      return items?.slice(6).map((item) => {
        return { ...item, value: item.key }
      })
    }
    return []
  }, [items])

  const showCoinList = useMemo(() => {
    if (items?.length > 7) {
      const temp = items.slice(0, 6)
      temp.push({ label: DEFAULTMORE, key: 'More', isHot: false })
      return temp
    } else {
      return items
    }

  }, [items])

  const changeHandler = (e) => {
    onChange?.(e)
  }

  // 判断当前选中token是否在隐藏列表中
  const isHidden = useMemo(() => {
    return hiddenCoinList.map(obj => obj.label).includes(current as string)
  }, [hiddenCoinList, current])

  // select中的value值
  const defaultSect = useMemo(() => {
    return isHidden ? current : t('more')
  }, [isHidden, current])


  // 渲染tabs列表
  const renderToTabs = (items) => {
    return items?.length > 7
      ?
      showCoinList.map((item, idx) => {
        if (item.label === DEFAULTMORE) {
          return <NoSSR key={item.label}>
            <Select
              width={140}
              height={48}
              value={defaultSect}
              size='medium'
              onChange={changeHandler}
              className={cls(styles.tabItem, {
                [styles.active]:
                  current != null && isHidden,
              })}
              optionClass={styles.textDown}
              options={hiddenCoinList}
            />
          </NoSSR>
        }
        return <div
          key={item.key}
          className={cls(styles.tabItem, {
            [styles.isHot]: !!item.isHot,
            [styles.active]:
              current != null ? current === item.key : idx === 0,
          })}
          onClick={() => {
            // setCurrent(item.key);
            onChange?.(item.key);
          }}
        >
          {item.label}
        </div>
      })
      :
      items.map((item, idx) => {
        return <div
          key={item.key}
          className={cls(styles.tabItem, {
            [styles.isHot]: !!item.isHot,
            [styles.active]:
              current != null ? current === item.key : idx === 0,
          })}
          onClick={() => {
            // setCurrent(item.key);
            onChange?.(item.key);
          }}
        >
          {item.label}
        </div>
      })
  }
  return (
    <div className={styles.tabs}>
      {
        renderToTabs(items)
      }
    </div>
  );
};

export const Banner: FC = () => {
  const store = useMarket();
  const { t, locale } = useI18n();
  const { check } = useCheckUser();

  return (
    <Block bgColor="#000" clsName={styles.banner}>
      <div className={styles.wrap}>
        <div className={styles.left}>
          <div className={styles.box}>
            <div className={styles.title}>{t('preMarketSpots')}</div>
            <div className={styles.content}>{t('preMarketTradingInfo')}</div>
            {/* <div className={styles.divider}></div> */}
            <div className={styles.actions}>
              <Action
                icon="https://s1.bycsi.com/bybit/deadpool/image-f921f812edc64b5c8a1809617f250a8e.svg"
                text={t('tradingRules')}
                onAction={() => {
                  tradeRuleClick();
                  window.open(
                    replaceDomainToLocation(
                      `https://www.bybit.com/${locale}/help-center/article/Introduction-to-Pre-market-Trading`
                    )
                  );
                }}
              />
              <Action
                icon="https://s1.bycsi.com/bybit/deadpool/image-62fe430ae0a942989207f056bd596d1e.svg"
                text={t('faq')}
                onAction={() => {
                  faqClick();
                  window.open(
                    replaceDomainToLocation(
                      `https://www.bybit.com/${locale}/help-center/article/FAQ-Pre-market-Trading`
                    )
                  );
                }}
              />
              <Action
                // icon="https://fh-static.bycsi.com/mannual-9f1d20f3b9f92af2cca8a5576fb0ca34.svg"
                icon="https://s1.bycsi.com/bybit/deadpool/image-96389ab17bda4838b8479dc0b1871e2c.svg"
                text={t('userGuide')}
                onAction={() => {
                  userGuideClick();
                  window.open(
                    replaceDomainToLocation(
                      `https://www.bybit.com/${locale}/help-center/article/How-to-Get-Started-With-Pre-market-Trading`
                    )
                  );
                }}
              />
              <Action
                icon="https://s1.bycsi.com/bybit/deadpool/image-35b5011d6e5641bfabf075112b9b7b1e.svg"
                text={t('newbieGuide')}
                isNew={!store.newUserGuideRead}
                onAction={() => {
                  newUserGuideClick();
                  if (check()) {
                    store.setDisplayNewUserGuide(true);
                  }
                }}
              />
            </div>
          </div>
          <section className={styles.projects}>
            <div>
              <p className={styles.val}>{store.tokenTradeCount?.itemsNum ? safeToFormat(store.tokenTradeCount?.itemsNum) : '--'}</p>
              <p className={styles.title}>{t('projects')}</p>
            </div>
            <p className={styles.line} />
            <div>
              <p className={styles.val}>{store.tokenTradeCount?.participantsNum ? safeToFormat(store.tokenTradeCount?.participantsNum) : '--'}</p>
              <p className={styles.title}>{t('participants')}</p>
            </div>
            <p className={styles.line} />
            <div>
              <p className={styles.val}>{store?.tokenTradeCount?.totalAmount ? `$${store.tokenTradeCount.totalAmount}` : '--'}</p>
              <p className={styles.title}>{t('totalTradingVolume')}</p>
            </div>
          </section>
          {/* <Tabs
          items={store.coinList || []}
          activeKey={store.currentCoin}
          onChange={(activeKey) => {

            store.setCurrentCoin?.(activeKey);
            store.setLastVisitToken?.(activeKey);
            // store.fetchTokenList();
          }}
        /> */}
        </div>
      </div>
    </Block>
  );
};
